import { num, type ComponentForm } from '../component-types.js';

/**
 * `canvas` (Unity's Canvas on a GameObject): shows a UI document
 * (`*.ui.json`, edited in the UI editor) while the entity exists.
 * Screen-space documents draw over the screen; `worldSpace` ones are a plane
 * in the world placed by the entity's transform (rotation and scale tilt and
 * size it, seen in perspective); `billboard` turns it to face the camera
 * (name plates, markers). See engine/docs/systems/ui.md.
 */
const form: ComponentForm = {
  key: 'canvas',
  label: 'Canvas (UI)',
  icon: 'ui',
  order: 88,
  defaults: { document: '' },
  fields: [
    { key: 'document', label: 'UI document', type: { kind: 'path', suffix: '.ui.json' }, hint: 'The *.ui.json to show. Open it in the Code tab to edit it visually.' },
    {
      key: 'renderMode',
      label: 'Render mode',
      type: { kind: 'enum', options: ['screenSpaceOverlay', 'screenSpaceCamera', 'worldSpace'] },
      hint: "Absent = the document's own render mode.",
    },
    { key: 'sortOrder', label: 'Sort order', type: num(1, { integer: true }), hint: "Added to the document's sort order; higher draws on top and gets input first." },
    {
      key: 'size',
      label: 'Size (units)',
      type: { kind: 'json' },
      hint: 'World space: canvas size [w, h] in canvas units (absent = the document\'s).',
      showIf: (v) => v.renderMode === 'worldSpace',
    },
    {
      key: 'pixelsPerUnit',
      label: 'Units per meter',
      type: num(10, { min: 1 }),
      hint: 'World space: canvas units per meter (absent = the document\'s).',
      showIf: (v) => v.renderMode === 'worldSpace',
    },
    {
      key: 'billboard',
      label: 'Face camera',
      type: { kind: 'bool' },
      hint: "World space: turn the canvas to face the camera instead of using the entity's rotation (absent = the document's).",
      showIf: (v) => v.renderMode === 'worldSpace',
    },
    {
      key: 'fadeDistance',
      label: 'Fade distance',
      type: { kind: 'json' },
      hint: "World space: [start, end] meters from the camera; opacity 1 → 0, hidden beyond (absent = the document's).",
      showIf: (v) => v.renderMode === 'worldSpace',
    },
    {
      key: 'occlusion',
      label: 'Occlusion',
      type: { kind: 'bool' },
      hint: "World space: hidden while colliders are between the camera and the canvas center (absent = the document's).",
      showIf: (v) => v.renderMode === 'worldSpace',
    },
    { key: 'visible', label: 'Visible', type: { kind: 'bool' }, read: (v) => v.visible ?? true },
    { key: 'interactable', label: 'Interactable', type: { kind: 'bool' }, read: (v) => v.interactable ?? true },
  ],
};

export default form;
